import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [creditAmounts, setCreditAmounts] = useState({});
  const [allocating, setAllocating] = useState(null);
  const [validityDays, setValidityDays] = useState({});
  const [extending, setExtending] = useState(null);

  const load = () => API.get('/admin/users').then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async id => {
    await API.post(`/admin/users/${id}/toggle`);
    setUsers(u => u.map(x => x.id === id ? { ...x, is_active: x.is_active ? 0 : 1 } : x));
    toast.success('User status updated');
  };

  const allocateCredits = async u => {
    const amount = parseInt(creditAmounts[u.id]);
    if (!Number.isInteger(amount) || amount <= 0) return toast.error('Enter a positive number of credits');
    setAllocating(u.id);
    try {
      const r = await API.post(`/admin/users/${u.id}/allocate-credits`, { amount });
      setUsers(us => us.map(x => x.id === u.id ? { ...x, ai_credit_limit: r.data.ai_credit_limit, ai_credits_used: r.data.ai_credits_used, sub_end_date: r.data.end_date, sub_status: r.data.status } : x));
      setCreditAmounts(a => ({ ...a, [u.id]: '' }));
      toast.success(`✅ +${amount} credits. ${u.full_name} now has ${r.data.creditsRemaining} remaining.`);
    } catch (err) { toast.error(err.response?.data?.error || 'Error allocating credits'); }
    finally { setAllocating(null); }
  };

  const extendValidity = async u => {
    const days = parseInt(validityDays[u.id]);
    if (!Number.isInteger(days) || days <= 0) return toast.error('Enter a positive number of days');
    setExtending(u.id);
    try {
      const r = await API.post(`/admin/users/${u.id}/set-validity`, { days });
      setUsers(us => us.map(x => x.id === u.id ? { ...x, sub_end_date: r.data.end_date, sub_status: r.data.status, ai_credit_limit: r.data.ai_credit_limit, ai_credits_used: r.data.ai_credits_used } : x));
      setValidityDays(d => ({ ...d, [u.id]: '' }));
      toast.success(`✅ +${days} days. ${u.full_name} now valid until ${new Date(r.data.end_date).toLocaleDateString()}.`);
    } catch (err) { toast.error(err.response?.data?.error || 'Error extending validity'); }
    finally { setExtending(null); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return toast.error('Min 6 characters');
    try {
      const r = await API.post(`/admin/users/${resetModal.id}/reset-password`, { new_password: newPassword });
      toast.success(`✅ Password reset! Share with ${resetModal.full_name}: ${newPassword}`);
      const waMsg = `Hello ${resetModal.full_name}, your new password for Oexams is: *${newPassword}*\nLogin at: ${window.location.origin}`;
      window.open(`https://wa.me/${resetModal.phone}?text=${encodeURIComponent(waMsg)}`);
      setResetModal(null); setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const roleLabel = { science_teacher:'🔬 Science Teacher', arts_teacher:'🎨 Arts Teacher', school:'🏫 School' };
  const filtered = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()) || u.phone.includes(search));

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'32px 20px' }}>
        <div className="page-header flex-between">
          <div><h1>👥 Users</h1><p>Manage all registered users</p></div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search..." style={{ width:240 }} />
        </div>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Name</th><th>Phone</th><th>Role</th><th>Subjects</th><th>Trial End</th><th>Status</th><th>Subscription Validity</th><th>AI Credits</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(u => {
                const subValid = u.sub_end_date && new Date(u.sub_end_date) > new Date();
                return (
                <tr key={u.id}>
                  <td style={{ fontWeight:600 }}>{u.full_name}</td>
                  <td style={{ fontFamily:'var(--mono)' }}>{u.phone}</td>
                  <td><span className="badge badge-primary">{roleLabel[u.role]||u.role}</span></td>
                  <td style={{ fontSize:12, color:'var(--text2)', maxWidth:200 }}>{u.subjects ? JSON.parse(u.subjects).join(', ') : '—'}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{u.trial_end ? new Date(u.trial_end).toLocaleDateString() : '—'}</td>
                  <td><span className={`badge ${u.is_active ? 'badge-success':'badge-danger'}`}>{u.is_active ? '✅ Active':'🚫 Disabled'}</span></td>
                  <td>
                    <span className={`badge ${subValid ? 'badge-success' : 'badge-danger'}`}>
                      {u.sub_end_date
                        ? (subValid ? `✅ expires ${new Date(u.sub_end_date).toLocaleDateString()}` : `⛔ expired ${new Date(u.sub_end_date).toLocaleDateString()}`)
                        : '— none'}
                    </span>
                    <div style={{ display:'flex', gap:4, marginTop:6 }}>
                      <input
                        type="number" min={1} placeholder="Days"
                        value={validityDays[u.id] || ''}
                        onChange={e => setValidityDays(d => ({ ...d, [u.id]: e.target.value }))}
                        style={{ width:64, padding:'4px 6px', fontSize:12 }}
                      />
                      <button
                        onClick={() => extendValidity(u)}
                        disabled={extending === u.id}
                        className="btn btn-primary btn-sm"
                      >{extending === u.id ? '⏳' : (subValid ? '⏩ Extend' : '🎁 Grant')}</button>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info" title="used / limit">🤖 {u.ai_credits_used || 0} / {u.ai_credit_limit || 0}</span>
                    <div style={{ display:'flex', gap:4, marginTop:6 }}>
                      <input
                        type="number" min={1} placeholder="Amt"
                        value={creditAmounts[u.id] || ''}
                        onChange={e => setCreditAmounts(a => ({ ...a, [u.id]: e.target.value }))}
                        style={{ width:64, padding:'4px 6px', fontSize:12 }}
                      />
                      <button
                        onClick={() => allocateCredits(u)}
                        disabled={allocating === u.id}
                        className="btn btn-primary btn-sm"
                      >{allocating === u.id ? '⏳' : '➕ Allocate'}</button>
                    </div>
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => { setResetModal(u); setNewPassword(''); }} className="btn btn-warning btn-sm">🔑 Reset PW</button>
                      <button onClick={() => toggle(u.id)} className={`btn btn-sm ${u.is_active ? 'btn-danger':'btn-success'}`}>{u.is_active ? 'Disable':'Enable'}</button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {resetModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>🔑 Reset Password</h2>
            <p style={{ color:'var(--text2)', marginBottom:20 }}>Resetting password for <strong>{resetModal.full_name}</strong> ({resetModal.phone})</p>
            <div className="form-group">
              <label>New Password *</label>
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" />
            </div>
            <div className="alert alert-info" style={{ fontSize:13, marginBottom:16 }}>
              💡 After resetting, WhatsApp will open automatically to send the new password to the teacher.
            </div>
            <div className="flex gap-2">
              <button onClick={resetPassword} className="btn btn-warning btn-full">🔑 Reset & Send via WhatsApp</button>
              <button onClick={() => setResetModal(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
